import { z } from 'zod';
/**
 * Kündigung von Verträgen (Modul `contracts`, siehe docs/vertraege.md). Eine Kündigung ist eine einseitige Erklärung, die wirksam wird, wenn sie dem anderen zugeht. Wirkia hilft deshalb
 * an zwei Stellen: Es berechnet Frist und Beendigungstermin, und es hält den Zugang beweisbar fest.
 *
 * Zwei Wege, je nach Form:
 * - **Schriftform** (`written`): Die Kündigung eines Arbeitsverhältnisses braucht die eigenhändige Unterschrift (§ 623 BGB), die elektronische Form ist ausgeschlossen. Wirkia erzeugt das
 *   Schreiben zum Ausdrucken, das Unternehmen unterschreibt es von Hand, übergibt es und erfasst danach den Zugang (Art, Datum, Zeuge) mit dem Scan des unterschriebenen Schreibens.
 * - **Elektronisch** (`electronic`): Nur für Verträge, die keine Schriftform verlangen. Das Unternehmen sendet die Kündigung mit Passwort, der Empfänger bestätigt den Zugang mit einem Code per E-Mail.
 *   Die Bestätigung ist keine Zustimmung zur Kündigung.
 * Geht der Arbeitnehmer oder Vertragspartner selbst, erfasst das Unternehmen den Eingang seines Schreibens mit dem Scan (`initiatedBy: 'party'`).
 */
export const TERMINATION_INITIATORS = ['company', 'party'];
export const TERMINATION_KINDS = ['ordinary', 'extraordinary'];
export const TERMINATION_KIND_LABELS = { ordinary: 'Ordentlich (mit Frist)', extraordinary: 'Außerordentlich (fristlos, aus wichtigem Grund)' };
export const TERMINATION_FORMS = ['written', 'electronic'];
export const TERMINATION_FORM_LABELS = { written: 'Schriftform (ausdrucken, unterschreiben, übergeben)', electronic: 'Elektronisch (Link und Code per E-Mail)' };
export const TERMINATION_STATUSES = ['draft', 'issued', 'delivered', 'withdrawn'];
export const TERMINATION_STATUS_LABELS = {
    draft: 'Entwurf',
    issued: 'Freigegeben, Zugang offen',
    delivered: 'Zugegangen',
    withdrawn: 'Zurückgezogen',
};
export const DELIVERY_METHODS = ['hand_witness', 'registered_mail', 'courier', 'bailiff', 'received', 'email_ack', 'email_viewed'];
export const DELIVERY_METHOD_LABELS = {
    hand_witness: 'Persönliche Übergabe mit Zeugen',
    registered_mail: 'Einschreiben (Einwurf oder Übergabe)',
    courier: 'Bote (Einwurf in den Briefkasten, mit Protokoll)',
    bailiff: 'Zustellung durch den Gerichtsvollzieher',
    received: 'Eingang des Schreibens beim Unternehmen',
    email_ack: 'Elektronisch, Empfang vom Empfänger bestätigt',
    email_viewed: 'Elektronisch, Link vom Empfänger geöffnet',
};
/** Wege, bei denen ein Scan des unterschriebenen Schreibens oder des Belegs dazugehört */
export const DELIVERY_NEEDS_SCAN = ['hand_witness', 'registered_mail', 'courier', 'bailiff', 'received'];
/** Was der Empfänger mit dem Code bestätigt. Wortgleich im Protokoll. */
export const ACK_STATEMENT = 'Ich bestätige, dass mir die Kündigung zugegangen ist. Diese Bestätigung ist keine Zustimmung zur Kündigung und gibt keine Rechte auf, sie bestätigt nur den Empfang. Ich gebe sie selbst ab und bestätige sie mit dem Code, den ich an meine E-Mail-Adresse erhalten habe.';
// ---------------------------------------------------------------------------
// Frist und Beendigungstermin
// ---------------------------------------------------------------------------
export const NOTICE_UNITS = ['days', 'weeks', 'months'];
export const NOTICE_ANCHORS = ['none', 'month_end', 'fifteenth_or_month_end'];
export const NOTICE_ANCHOR_LABELS = { none: 'Ohne festen Termin', month_end: 'Zum Monatsende', fifteenth_or_month_end: 'Zum 15. oder zum Monatsende' };
export const noticeRuleInput = z.object({
    /** Wer kündigt: Der Arbeitgeber (Unternehmen) oder der Arbeitnehmer. Bei anderen Verträgen zählt nur die vereinbarte Frist. */
    by: z.enum(['employer', 'employee']).default('employer'),
    /** Probezeit (höchstens sechs Monate): Es gilt eine Frist von zwei Wochen, § 622 Abs. 3 BGB */
    probation: z.boolean().default(false),
    /** Beginn des Arbeitsverhältnisses, für die Staffelung der Frist des Arbeitgebers nach der Betriebszugehörigkeit, § 622 Abs. 2 BGB */
    employedSince: z.iso.date().nullable().optional(),
    /** Vereinbarte Frist. Sie ersetzt die gesetzliche, wo das zulässig ist. Ohne Angabe gilt bei Arbeitsverträgen die gesetzliche Frist. */
    custom: z
        .object({ value: z.number().int().min(0).max(120), unit: z.enum(NOTICE_UNITS), anchor: z.enum(NOTICE_ANCHORS) })
        .nullable()
        .optional(),
});
const pad = (n) => String(n).padStart(2, '0');
const toIso = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const parse = (iso) => new Date(`${iso}T00:00:00Z`);
const lastDayOfMonth = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
/** Addiert Monate. Gibt es den Tag im Zielmonat nicht, gilt der letzte Tag des Monats (§ 188 Abs. 3 BGB). */
function addMonths(iso, months) {
    const d = parse(iso);
    const total = d.getUTCFullYear() * 12 + d.getUTCMonth() + months;
    const y = Math.floor(total / 12);
    const m = total % 12;
    return toIso(new Date(Date.UTC(y, m, Math.min(d.getUTCDate(), lastDayOfMonth(y, m)))));
}
const addDays = (iso, days) => toIso(new Date(parse(iso).getTime() + days * 86_400_000));
/** Der erste Termin am oder nach dem Tag, der zum Anker passt */
function anchorOn(iso, anchor) {
    if (anchor === 'none')
        return iso;
    const d = parse(iso);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const end = toIso(new Date(Date.UTC(y, m, lastDayOfMonth(y, m))));
    if (anchor === 'month_end')
        return end;
    return d.getUTCDate() <= 15 ? toIso(new Date(Date.UTC(y, m, 15))) : end;
}
/** Volle Jahre zwischen zwei Tagen */
function fullYears(from, to) {
    const a = parse(from);
    const b = parse(to);
    let years = b.getUTCFullYear() - a.getUTCFullYear();
    if (b.getUTCMonth() < a.getUTCMonth() || (b.getUTCMonth() === a.getUTCMonth() && b.getUTCDate() < a.getUTCDate()))
        years -= 1;
    return Math.max(0, years);
}
/** Gesetzliche Fristen des Arbeitgebers nach Betriebszugehörigkeit, § 622 Abs. 2 BGB: Monate zum Monatsende */
const EMPLOYER_STEPS = [[20, 7], [15, 6], [12, 5], [10, 4], [8, 3], [5, 2], [2, 1]];
const unitLabel = (value, unit) => `${value} ${unit === 'days' ? (value === 1 ? 'Tag' : 'Tage') : unit === 'weeks' ? (value === 1 ? 'Woche' : 'Wochen') : value === 1 ? 'Monat' : 'Monate'}`;
/**
 * Beendigungstermin bei ordentlicher Kündigung. Die Frist beginnt am Tag nach dem Zugang (§ 187 Abs. 1 BGB), deshalb zählt der Tag des Zugangs, nicht der des Schreibens. Gibt es eine
 * vereinbarte Frist, gilt sie, sonst bei Arbeitsverträgen die gesetzliche (§ 622 BGB). Die Berechnung ist eine Hilfe: Tarifverträge, Betriebsvereinbarungen und Sonderfälle
 * (Schwerbehinderung, Elternzeit) kennt sie nicht, die Oberfläche weist darauf hin.
 */
export function noticeEffectiveDate(deliveryDate, rule, employment) {
    if (rule.custom) {
        const { value, unit, anchor } = rule.custom;
        const base = unit === 'days' ? addDays(deliveryDate, value) : unit === 'weeks' ? addDays(deliveryDate, value * 7) : addMonths(deliveryDate, value);
        return { date: anchorOn(base, anchor), label: `${unitLabel(value, unit)} (vereinbart), ${NOTICE_ANCHOR_LABELS[anchor].toLowerCase()}` };
    }
    if (!employment) {
        // Ohne vereinbarte Frist gibt es für andere Verträge keine feste gesetzliche. Wirkia nimmt eine Mindestfrist von vier Wochen zum Monatsende als Vorschlag.
        return { date: anchorOn(addDays(deliveryDate, 28), 'month_end'), label: '4 Wochen zum Monatsende (Vorschlag, keine gesetzliche Frist: bitte Vertrag prüfen)' };
    }
    if (rule.probation)
        return { date: addDays(deliveryDate, 14), label: '2 Wochen (Probezeit, § 622 Abs. 3 BGB)' };
    if (rule.by === 'employee')
        return { date: anchorOn(addDays(deliveryDate, 28), 'fifteenth_or_month_end'), label: '4 Wochen zum 15. oder zum Monatsende (§ 622 Abs. 1 BGB)' };
    const years = rule.employedSince ? fullYears(rule.employedSince, deliveryDate) : 0;
    const step = EMPLOYER_STEPS.find(([y]) => years >= y);
    if (!step)
        return { date: anchorOn(addDays(deliveryDate, 28), 'fifteenth_or_month_end'), label: '4 Wochen zum 15. oder zum Monatsende (§ 622 Abs. 1 BGB)' };
    return { date: anchorOn(addMonths(deliveryDate, step[1]), 'month_end'), label: `${unitLabel(step[1], 'months')} zum Monatsende (§ 622 Abs. 2 BGB, ${years} Jahre Betriebszugehörigkeit)` };
}
/**
 * Kündigungsschreiben des Unternehmens als Ausgangstext. Bei der ordentlichen Kündigung steht „hilfsweise zum nächstmöglichen Zeitpunkt“ dabei, damit sie wirkt, auch wenn der genannte
 * Termin nicht stimmt. Die außerordentliche Kündigung enthält vorsorglich die ordentliche (§ 140 BGB). Der Text ist ein Muster und ersetzt keine Rechtsberatung.
 */
export function terminationText(i) {
    if (i.initiatedBy === 'party')
        return '';
    const head = `${i.companyName}\n${i.companyAddress}\n\nAn\n${i.partyName}\n\n${i.today}\n\nKündigung des Vertrags „${i.contractTitle}“ (${i.contractNumber}) vom ${i.contractDate}\n\nSehr geehrte Damen und Herren,\n\n`;
    const reason = i.reason ? `\n\nBegründung: ${i.reason}` : '';
    const core = i.kind === 'extraordinary'
        ? `hiermit kündigen wir den oben genannten Vertrag außerordentlich und fristlos. Vorsorglich kündigen wir ihn außerdem ordentlich zum ${i.effective}, hilfsweise zum nächstmöglichen Zeitpunkt.${reason}`
        : `hiermit kündigen wir den oben genannten Vertrag ordentlich und fristgerecht zum ${i.effective}, hilfsweise zum nächstmöglichen Zeitpunkt.${reason}`;
    const employment = i.employment
        ? '\n\nHinweis zur Arbeitsuche: Sie sind verpflichtet, sich spätestens drei Tage nach Zugang dieser Kündigung persönlich bei der Agentur für Arbeit arbeitsuchend zu melden (§ 38 Abs. 1 SGB III). Verspätetes Melden kann zu einer Sperre des Arbeitslosengelds führen. Bitte nehmen Sie außerdem Ihren Resturlaub und geben Sie Arbeitsmittel bis zum Ende des Arbeitsverhältnisses zurück. Ein Arbeitszeugnis stellen wir auf Wunsch aus.'
        : '';
    return `${head}${core}${employment}\n\nMit freundlichen Grüßen`;
}
/** Hinweise in der Maske. Sie ersetzen keine Rechtsberatung, sie nennen die häufigsten Stolpersteine. */
export const TERMINATION_HINTS = {
    employment: [
        'Die Kündigung braucht die eigenhändige Unterschrift einer vertretungsberechtigten Person auf dem Papier (§ 623 BGB). Eine E-Mail, ein Foto oder eine elektronische Unterschrift genügt nicht.',
        'Mit einem Betriebsrat: Er ist vor jeder Kündigung anzuhören (§ 102 BetrVG), sonst ist sie unwirksam.',
        'Besonderen Schutz genießen Schwerbehinderte (Zustimmung des Integrationsamts), Schwangere und Mütter, Beschäftigte in Elternzeit sowie Auszubildende nach der Probezeit. Prüfen Sie das vor der Kündigung.',
        'Im Betrieb mit mehr als zehn Beschäftigten und nach sechs Monaten gilt das Kündigungsschutzgesetz: Die ordentliche Kündigung braucht einen Grund (verhaltens-, personen- oder betriebsbedingt). Der Arbeitnehmer kann nur drei Wochen nach Zugang Klage erheben, deshalb ist der Zugangstag so wichtig.',
        'Die außerordentliche Kündigung muss innerhalb von zwei Wochen nach Kenntnis des Grundes zugehen (§ 626 Abs. 2 BGB).',
    ],
    other: [
        'Prüfen Sie im Vertrag, wie er zu kündigen ist (Form, Frist, Termin). Eine vereinbarte Schriftform lässt sich nicht durch eine E-Mail ersetzen, wenn der Vertrag sie verlangt.',
        'Die Kündigung wirkt erst, wenn sie dem anderen zugeht. Sie trägt die Beweislast dafür, dass sie zugegangen ist.',
    ],
};
// ---------------------------------------------------------------------------
// Eingaben und Verträge der Schnittstelle
// ---------------------------------------------------------------------------
const text = (max) => z.string().trim().max(max);
const isoDate = z.iso.date('Bitte geben Sie ein gültiges Datum an.');
export const createTerminationInput = z.object({
    initiatedBy: z.enum(TERMINATION_INITIATORS),
    kind: z.enum(TERMINATION_KINDS).default('ordinary'),
    form: z.enum(TERMINATION_FORMS),
    reason: text(2000).nullable().optional(),
    /** Tag, an dem die Kündigung voraussichtlich zugeht. Daraus entsteht der Beendigungstermin im Schreiben. */
    plannedDeliveryDate: isoDate,
    rule: noticeRuleInput.default({ by: 'employer', probation: false }),
    /** Eigener Text des Schreibens, sonst entsteht er aus der Vorlage */
    noticeText: z.string().trim().max(20000).nullable().optional(),
    /** Frei gewählter Beendigungstermin statt des berechneten, nur für die ordentliche Kündigung und nicht vor dem berechneten */
    effectiveDate: isoDate.nullable().optional(),
});
export const updateTerminationInput = createTerminationInput.partial().refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const issueTerminationInput = z.object({ password: z.string().min(1, 'Bitte geben Sie Ihr Passwort ein.').max(200) });
/** Zugang erfassen. Die Datei (Scan des unterschriebenen Schreibens oder des Belegs) kommt als Anfragekörper, diese Angaben als Abfrageparameter. */
export const recordDeliveryQuery = z.object({
    deliveryDate: isoDate,
    method: z.enum(DELIVERY_METHODS),
    /** Nur beim Eingang einer Kündigung des Vertragspartners: der Termin, der in seinem Schreiben steht */
    effectiveDate: isoDate.optional(),
    witness: text(200).optional(),
    note: text(1000).optional(),
});
export const withdrawTerminationInput = z.object({ reason: text(500).optional() });
export const ackCodeInput = z.object({ name: z.string().trim().min(2, 'Der Name ist zu kurz.').max(160) });
export const ackInput = z.object({
    name: z.string().trim().min(2, 'Der Name ist zu kurz.').max(160),
    code: z.string().trim().regex(/^\d{6}$/, 'Der Code besteht aus sechs Ziffern.'),
    consent: z.literal(true, { error: 'Bitte bestätigen Sie die Erklärung.' }),
});
//# sourceMappingURL=terminations.js.map